import { useState, useCallback } from "react";
import { deleteSessionDraft, listSessionDrafts, saveSessionDraft } from "@/lib/sessionDraftStore";
import { draftListQuery, draftSaveBody } from "@/lib/portalDraftRequests";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";

// Drafts of in-progress forms.
//
// These used to live in the browser tab, so closing a laptop lost the work while the button
// said "Save draft". They now persist through /api/portal-drafts: your own, your firm's if
// you supervise, never another firm's, and expiring 60 days after the last edit.
//
// Two cases still use the browser store, because both lack a server session to save
// against, not because their drafts matter less:
//
//   - LACW local test mode, which fabricates a user client-side.
//   - Admin billing sheets, deliberately kept on the one computer via the device-storage
//     allowlist. They already survived a tab closing, so they are not what this fixed.
//
// The name is now accurate for the first time: this really does reach the cloud.

function usesBrowserStore(formType, brand) {
  if (brand === "lacw" && isLacwLocalTestMode()) return true;
  return formType === "billing_sheet" && brand === "admin";
}

// The server names a draft's owner; the browser store called them the saver. Kept in the
// store's shape so every page reading a draft carries on working unchanged.
function fromServer(row) {
  if (!row || typeof row !== "object") return row;
  return {
    id: row.id,
    form_type: row.form_type,
    draft_name: row.draft_name,
    saved_by: row.owner_name || row.owner_email || "",
    saved_by_email: row.owner_email || "",
    brand: row.brand,
    form_data: row.form_data,
    matter_id: row.matter_id ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    expires_at: row.expires_at,
  };
}

async function readError(response, fallback) {
  const payload = await response.json().catch(() => ({}));
  return new Error(payload.error || fallback);
}

export function useCloudDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDrafts = useCallback(async (formType, brand = "vals") => {
    setIsLoading(true);
    setError("");
    try {
      if (usesBrowserStore(formType, brand)) {
        const data = listSessionDrafts(formType, brand);
        setDrafts(data);
        return data;
      }

      const response = await fetch("/api/portal-drafts?" + draftListQuery(formType, brand), {
        credentials: "same-origin",
      });
      if (!response.ok) throw await readError(response, "Failed to load drafts");

      const rows = await response.json();
      const data = (Array.isArray(rows) ? rows : []).map(fromServer);
      setDrafts(data);
      return data;
    } catch (err) {
      console.error("Error fetching drafts:", err);
      setError(err.message || "Failed to load drafts");
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveDraft = useCallback(async ({ id, formType, draftName, savedBy, savedByEmail, brand = "vals", formData, matter_id }) => {
    setIsLoading(true);
    setError("");
    try {
      if (usesBrowserStore(formType, brand)) {
        const saved = saveSessionDraft({ id, formType, draftName, savedBy, savedByEmail, brand, formData, matter_id });
        await fetchDrafts(formType, brand);
        return saved;
      }

      const response = await fetch("/api/portal-drafts", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draftSaveBody({ id, formType, draftName, brand, formData, matter_id })),
      });
      // Thrown rather than swallowed: someone who believes a draft saved and finds it gone
      // is worse off than someone told it did not.
      if (!response.ok) throw await readError(response, "Failed to save draft");

      const saved = fromServer(await response.json());
      await fetchDrafts(formType, brand);
      return saved;
    } catch (err) {
      console.error("Error saving draft:", err);
      setError(err.message || "Failed to save draft");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchDrafts]);

  const deleteDraft = useCallback(async (id, formType, brand = "vals") => {
    setIsLoading(true);
    setError("");
    try {
      if (usesBrowserStore(formType, brand)) {
        deleteSessionDraft(id, formType, brand);
        await fetchDrafts(formType, brand);
        return;
      }

      const response = await fetch("/api/portal-drafts?id=" + encodeURIComponent(id), {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!response.ok) throw await readError(response, "Failed to delete draft");
      await fetchDrafts(formType, brand);
    } catch (err) {
      console.error("Error deleting draft:", err);
      setError(err.message || "Failed to delete draft");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchDrafts]);

  return { drafts, isLoading, error, fetchDrafts, saveDraft, deleteDraft };
}
